import _ from 'lodash';

/**
 * Translates a stored filter's sort direction into the sortOrder value an Ant Design column expects. Yields ascend or descend when the filter sorts by this column, or when the column check is disabled, and null otherwise.
 *
 * @param dataIndex - Column data index being rendered.
 * @param sortedInfo - Active sort state with sortBy and sortOrder.
 * @param isCheckSortBy - Whether the sorted column must match dataIndex.
 * @returns The column sort order, or null when the column is not sorted.
 */
const convertSortOrder = (dataIndex: any, sortedInfo: any, isCheckSortBy = true) => {
  if (sortedInfo?.sortBy === dataIndex || !isCheckSortBy) {
    if (sortedInfo?.sortOrder === 'asc') {
      return 'ascend';
    }
    if (sortedInfo?.sortOrder === 'desc') {
      return 'descend';
    }
  }
  return null;
};

/**
 * Decides whether an Ant Design table sorter differs from the filter currently applied. Treats a cleared order as changed, maps ascend and descend onto asc and desc for comparison with the filter's sortOrder, and also compares the sorted field against sortBy unless that check is disabled.
 *
 * @param sorter - Sorter descriptor with field and order.
 * @param currentFilter - Active filter holding sortBy and sortOrder.
 * @param isCheckSortBy - Whether the sorted column must also match.
 * @returns True when the sorter differs from the current filter.
 */
const checkIsChangedSorter = (sorter: any, currentFilter: any, isCheckSortBy = true) => {
  const { field, order } = sorter || {};

  if (!order) {
    return true;
  }

  const sortOrder = order === 'ascend' ? 'asc' : 'desc';

  if (sortOrder !== currentFilter?.sortOrder) {
    return true;
  }

  if (field !== currentFilter?.sortBy && isCheckSortBy) {
    return true;
  }

  return false;
};

/**
 * Comparator ordering two table rows by a numeric field. Reads the key from both rows with lodash get, pushes non-numeric values to the end by returning -1 or 1, and otherwise returns the difference of the two numbers.
 *
 * @param a - First row.
 * @param b - Second row.
 * @param key - Path of the numeric field to compare.
 * @returns A negative, zero, or positive comparison result.
 */
const sortNumber = (a: any, b: any, key: any) => {
  const value1 = _.get(a, key);
  const value2 = _.get(b, key);

  if (!_.isNumber(value1)) {
    return -1;
  }

  if (!_.isNumber(value2)) {
    return 1;
  }

  if (value1 === value2) {
    return 0;
  }

  return value1 - value2;
};

/**
 * Comparator ordering two table rows by a string field. Reads the key from both rows with lodash get, pushes non-string values to the end by returning -1 or 1, and otherwise compares with localeCompare.
 *
 * @param a - First row.
 * @param b - Second row.
 * @param key - Path of the string field to compare.
 * @returns A negative, zero, or positive comparison result.
 */
const sortString = (a: any, b: any, key: any) => {
  const value1 = _.get(a, key);
  const value2 = _.get(b, key);

  if (!_.isString(value1)) {
    return -1;
  }

  if (!_.isString(value2)) {
    return 1;
  }

  return value1.localeCompare(value2);
};

export { checkIsChangedSorter, convertSortOrder, sortNumber, sortString };
